import React, { useCallback, useMemo } from 'react';
import { AccommodationsConsoleWithHealth } from '../../AccommodationsConsole';
import { HONEYMOON_AVAILABLE_PRIORITIZE_TAGS, HONEYMOON_PRIORITIZE_TAGS, HotelSearchMapLayout } from '@shared/constants/accommodations';
import type { ScenarioConsoleProps, ScenarioConfig } from '../scenarios.types';
import type { TopBarConfig } from '@shared/types/Accommodations.types';
import { OtaListingEntry } from '../components/listing-entry/OtaListingEntry';
import type { ScenarioListingEntryProps } from '../components/listing-entry/ListingEntry.types';

type HoneymoonConsoleProps = Readonly<
  ScenarioConsoleProps & {
    layout?: HotelSearchMapLayout;
  }
>;

/**
 * The tag this page exists to lead with, and the one choice its tag filter offers.
 *
 * Seeded into the URL on mount when a link omits `?prioritizeTag=`, so the page still leads with
 * honeymoon stays by default — but the guest can uncheck it, which a value merged in at read time
 * could never allow (see useSeedDefaultPrioritizeTags).
 *
 * Only the honeymoon tag is seeded. The trip types (`TRIP_TYPE_TAGS`) are offered alongside it in
 * the filter but never seeded: they narrow the honeymoon premise, and a page that opened with one
 * pre-ticked would hide most of the destination for no reason the guest asked for.
 */

const HONEYMOON_TOP_BAR: TopBarConfig = {
  searchForm: { dateRangeEnabled: true, occupancySelectorEnabled: true },
  showJoyLogo: true,
  showCloseButton: false,
  showTravelProductToggle: true,
  hideCarsPromoTooltip: true,
};

/**
 * Standalone honeymoon hotel search.
 *
 * A scenario rather than a forked console: `guest`, `admin`, `ota` and `cashFund` already express
 * "same search surface, different product" this way, and the honeymoon page differs only in which
 * stays lead. Forking would duplicate the map, filters, pricing and sort behaviour and leave two
 * copies to keep in step.
 *
 * Listing entries reuse the OTA renderer: like OTA this page is reached from outside an event, so
 * there is no couple context and no "add to my event" CTA to render.
 */
export const HoneymoonAccommodationsConsole = ({ eventHandle, eventName, onClose, selectedHotelId, layout = HotelSearchMapLayout.COMPACT }: HoneymoonConsoleProps) => {
  const scenarioConfig = useMemo<ScenarioConfig>(
    () => ({
      variant: 'honeymoon',
      topBar: HONEYMOON_TOP_BAR,
      mapLayoutMode: layout,
      defaultPrioritizeTags: HONEYMOON_PRIORITIZE_TAGS,
      availablePrioritizeTags: HONEYMOON_AVAILABLE_PRIORITIZE_TAGS,
    }),
    [layout],
  );

  const renderListingEntry = useCallback((props: ScenarioListingEntryProps) => <OtaListingEntry {...props} />, []);

  return (
    <AccommodationsConsoleWithHealth
      eventHandle={eventHandle}
      eventName={eventName}
      onClose={onClose}
      selectedHotelId={selectedHotelId}
      scenarioConfig={scenarioConfig}
      renderListingEntry={renderListingEntry}
    />
  );
};
